import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { EventCard, FilterType } from "../models/enums";
import { Filter } from "../models/event/filter";
import { TimeType } from "../models/timeType";
import { AnnouncementCategory } from "../models/announcement/announcement-category";

/** Owns active filter state and notifies listeners of changes / side-effect triggers. */
@Injectable({
  providedIn: "root",
})
export class FilterService {
  public currentState!: EventCard;

  private filter!: Filter;
  /** View filter for traffic-action announcement categories (categoryType strings). */
  private categoryFilter: string[] = [];

  private readonly filterChanged = new Subject<Filter>();
  private readonly resetFilterTrigger = new Subject<boolean>();
  private readonly fetchTrainsTrigger = new Subject<boolean>();
  private readonly changeCategoryFilterTrigger = new Subject<boolean>();
  private readonly updateEventListOnFiltering = new Subject<boolean>();
  private readonly currentOpenedFilterChanged = new Subject<FilterType>();
  private readonly stationCodesChanged = new Subject<string[]>();
  private readonly announcementCategoryChanged =
    new Subject<AnnouncementCategory[]>();

  constructor() {
    this.setDefaultFilter();
  }

  public getFilter(): Filter {
    return this.filter;
  }

  public getFilterCopy() {
    const filterCopy = JSON.parse(JSON.stringify(this.filter));
    filterCopy.id = 0;
    return filterCopy;
  }

  public getCategoryFilter() {
    return this.categoryFilter;
  }

  /**
   * Switch filter to use for the given card.
   * @param newFilter optional filter to inherit (timestamps, stations, trains, etc.)
   */
  public switchFilterState(
    newCard: EventCard,
    removeOldFilter: boolean = false,
    newFilter?: Filter
  ): void {
    this.currentState = newCard;
    if (newFilter) {
      this.filter = newFilter;
    }

    if (newCard == EventCard.EventList && !removeOldFilter) {
      this.setDefaultFilter();
    }
    this.filterChanged.next(this.filter);
  }

  public isDisabled(): boolean {
    return this.currentState == EventCard.Announcement;
  }

  /** True when any filter differs from the default “show all” state. */
  public hasActiveFilters(): boolean {
    const f = this.filter;
    if (!f) {
      return false;
    }
    return (
      (f.trainNumbers?.length ?? 0) > 0 ||
      (f.lines?.length ?? 0) > 0 ||
      !f.southGoing ||
      !f.northGoing ||
      f.timeType?.id !== 1
    );
  }

  public getCurrentOpenedFilterChanged(): Observable<FilterType> {
    return this.currentOpenedFilterChanged;
  }

  public setCurrentFilterType(filter: FilterType) {
    this.currentOpenedFilterChanged.next(filter);
  }

  public getUpdateEventsOnFilteringChange() {
    return this.updateEventListOnFiltering;
  }

  public getFilterChanges(): Observable<Filter> {
    return this.filterChanged;
  }

  /** @deprecated Prefer getFilterChanges(); kept as alias for existing callers. */
  public getAllUpdatedFilters(): Observable<Filter> {
    return this.filterChanged;
  }

  public resetFilter() {
    this.setDefaultFilter();
    this.resetFilterTrigger.next(true);
    this.updateEventListOnFiltering.next(true);
    this.emitFromFiltering();
    this.setCategoryFilter(this.categoryFilter);
  }

  public setDates(fromDate: Date, toDate: Date): void {
    this.filter.fromDate = fromDate;
    this.filter.toDate = toDate;
    this.afterFilterChange();
  }

  public setTimeType(timeType: TimeType): void {
    this.filter.timeType = timeType;
    this.afterFilterChange();
  }

  public setTrainNumbers(trainNumbers: string[]): void {
    this.filter.trainNumbers = trainNumbers;
    this.afterFilterChange();
  }

  public setLines(lines: string[]): void {
    this.filter.lines = lines;
    this.afterFilterChange();
  }

  public setSouthGoing(value: boolean): void {
    this.filter.southGoing = value;
    this.afterFilterChange();
  }

  public setNorthGoing(value: boolean): void {
    this.filter.northGoing = value;
    this.afterFilterChange();
  }

  public setStationCodes(stationCodes: string[]): void {
    this.filter.stationCodes = stationCodes;
    this.stationCodesChanged.next(stationCodes);
    this.afterFilterChange();
  }

  public setAnnouncementCategories(categories: AnnouncementCategory[]): void {
    this.filter.announcementCategories =
      categories.length > 0 ? categories.map((c) => c.id) : [0];
    this.announcementCategoryChanged.next(categories);
    this.afterFilterChange();
  }

  public setCategoryFilter(value: string[]): void {
    this.categoryFilter = value;
    this.changeCategoryFilterTrigger.next(true);
  }

  public getAnnouncementCategoryChange(): Observable<AnnouncementCategory[]> {
    return this.announcementCategoryChanged;
  }

  public getCategoryChangeTrigger(): Observable<boolean> {
    return this.changeCategoryFilterTrigger;
  }

  public getStationCodesChange(): Observable<string[]> {
    return this.stationCodesChanged;
  }

  public getResetFilterTrigger(): Observable<boolean> {
    return this.resetFilterTrigger;
  }

  public getFetchTrainsTrigger(): Observable<boolean> {
    return this.fetchTrainsTrigger;
  }

  private setDefaultFilter() {
    const MS_PER_MINUTE = 60000;
    const now = new Date();
    now.setSeconds(0);
    now.setMilliseconds(0);

    const fromTime = new Date(now.getTime());
    const toTime = new Date(now.getTime() + 1 * 60 * MS_PER_MINUTE);
    this.filter = new Filter({
      id: null as unknown as number,
      fromDate: fromTime,
      toDate: toTime,
      timeType: new TimeType(1, "Planned"),
      stationCodes: [],
      lines: [],
      trainNumbers: [],
      southGoing: true,
      northGoing: true,
      arrivingTrains: false,
      departingTrains: true,
      announcementCategories: [0],
    });
    this.categoryFilter = [];
  }

  private afterFilterChange() {
    this.filterChanged.next(this.filter);
    this.emitFromFiltering();
  }

  private emitFromFiltering() {
    switch (this.currentState) {
      case EventCard.EventList:
        this.updateEventListOnFiltering.next(true);
        break;
      case EventCard.EventForm:
      case EventCard.TrafficActionForm:
      case EventCard.Announcement:
        this.fetchTrainsTrigger.next(true);
        break;
      default:
        break;
    }
  }
}
